import { ActionIconProps, Mark, Text, UnstyledButton } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, forwardRef } from "react";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { deleteQuestionario } from "../../firestore";
import { QUERY_KEY } from "../questionario-table/QuestionarioTable";

type RemoveButtonProperties = PropsWithChildren<
  ActionIconProps & { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; questionario: TQuestionario }
>;
type TDeleteResponse = Awaited<ReturnType<typeof deleteQuestionario>>;

const RemoveQuestionarioButton = forwardRef<HTMLButtonElement, RemoveButtonProperties>(
  ({ questionario, children, ...properties }, reference) => {
    const queryClient = useQueryClient();
    const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

    const { mutate: deleteQuestionarioMutation, isPending } = useMutation<TDeleteResponse, Error>({
      mutationFn: () => {
        if (!activeEmpresaId) {
          throw new Error("Empresa não selecionada");
        }
        return deleteQuestionario(activeEmpresaId, questionario);
      },
      onSuccess() {
        return queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
      onError(error) {
        captureException(error, true);
      }
    });

    /**
     * Handles the click event on the button.
     * @param event - The event that triggered the click.
     */
    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();

      if (properties.onClick) {
        properties.onClick(event);
      }

      modals.openConfirmModal({
        title: "Remover questionario",
        centered: true,
        children: (
          <Text size="sm">
            Tem certeza que deseja remover o questionario <Mark>{questionario.nome}</Mark>
          </Text>
        ),
        labels: {
          confirm: "Sim",
          cancel: "Não"
        },
        confirmProps: { color: "red" },
        onConfirm: () => {
          void deleteQuestionarioMutation();
        }
      });
    }

    return (
      <UnstyledButton disabled={isPending} ref={reference} {...properties} onClick={handleClick}>
        {children}
      </UnstyledButton>
    );
  }
);

RemoveQuestionarioButton.displayName = "RemoveQuestionarioButton";

export default RemoveQuestionarioButton;
